import { useLayoutEffect, useRef, useState } from 'react';
import type { StoryScene } from '@/content/story';
import { cssVars } from '@/lib/cssVars';
import { cx } from '@/lib/cx';
import type { Orientation } from '@/lib/orientation';
import { quadMatrix, quadSize, type Point } from './perspective';
import {
  alexImage,
  FOE,
  LAYOUT,
  POSES,
  roomImage,
  SCENES,
  type Box,
  type Quad,
} from './sceneLayers';
import styles from './SceneStage.module.css';

/** A quad in percent of the stage, in pixels of a stage `size`. */
function onStage(quad: Quad, [w, h]: [number, number]): [Point, Point, Point, Point] {
  const px = ([x, y]: readonly [number, number]): Point => [(x / 100) * w, (y / 100) * h];
  return [px(quad[0]), px(quad[1]), px(quad[2]), px(quad[3])];
}

const percent = (box: Box) => ({
  left: `${String(box.left)}%`,
  top: `${String(box.top)}%`,
  width: `${String(box.width)}%`,
  height: `${String(box.height)}%`,
});

/**
 * The intro's scene, built from layers so only what changes changes (QA 2026-10-11): the empty HQ
 * room, the first foe shown on its big screen, and Agent Alex in front, his expression swapped on
 * one shared canvas. The stage keeps the painting's shape and covers the viewport, so every layer
 * stays on its spot at any crop. The camera is never reset by a change: a slow drift runs
 * underneath the whole time, and the close-up zooms in and back out smoothly on its own wrapper
 * (QA 2026-10-11: a zoom that snapped back when the picture changed was jarring). `wipe` (a new
 * number per reveal) sweeps a diagonal bar across the screen as the foe appears.
 */
export function SceneStage({
  orientation,
  scene,
  wipe,
}: {
  orientation: Orientation;
  scene: StoryScene;
  wipe: number;
}) {
  const room = roomImage(orientation);
  const shot = SCENES[scene];
  const layout = LAYOUT[orientation];
  // The stage's size in pixels, for the perspective of the angled screen.
  const stageRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<[number, number]>([0, 0]);
  useLayoutEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const measure = () => {
      setSize([el.offsetWidth, el.offsetHeight]);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, []);
  const glass = onStage(layout.screen, size);
  const [glassW, glassH] = quadSize(glass);
  if (!room) return null;
  return (
    <div className={styles.viewport} aria-hidden>
      <div
        ref={stageRef}
        className={styles.stage}
        style={cssVars({ '--aspect': room.width / room.height, '--anchor-x': layout.anchorX })}
      >
        <div
          className={cx(styles.zoom, shot.close && styles.close)}
          style={cssVars({
            '--face-x': `${String(layout.face[0])}%`,
            '--face-y': `${String(layout.face[1])}%`,
          })}
        >
          <div className={styles.drift}>
            <img src={room.url} alt="" className={styles.room} draggable={false} />
            {/* Content laid flat at the glass's own size, then set onto its angled corners. */}
            <div
              className={styles.screen}
              style={{
                width: glassW || 1,
                height: glassH || 1,
                transform: quadMatrix(glassW || 1, glassH || 1, glass),
              }}
            >
              {shot.foe && FOE && (
                <img
                  src={FOE.portraits.disguised.url}
                  alt=""
                  className={styles.foe}
                  draggable={false}
                />
              )}
              {wipe > 0 && (
                <div key={wipe} className={styles.wipe}>
                  <div className={styles.sweep}>
                    <span className={styles.bar} />
                    <span className={styles.edge} />
                  </div>
                </div>
              )}
            </div>
            {/* Every expression stays loaded and drawn; a change crossfades between them, so no
                frame is ever empty (QA 2026-10-12: a new image flashed while it loaded). */}
            {POSES.map((pose) => {
              const alex = alexImage(pose);
              return (
                alex && (
                  <img
                    key={pose}
                    src={alex.url}
                    alt=""
                    className={cx(styles.alex, pose === shot.alex && styles.shown)}
                    style={percent(layout.alex)}
                    draggable={false}
                    decoding="sync"
                  />
                )
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
